<template>
    <div class="print-container" :style="paperSizeStyle">
        <!--Header-->
        <div class="header">
            <div>
                <div class="title">{{ getUserInfo.organizationName }}</div>
                <div class="title">{{ panelTitle }}</div>
            </div>
            <div class="logo-container">
                <img
                    :src="menuComponents.logo.imageUrl"
                    :alt="menuComponents.logo.logo"
                    class="image"
                />
            </div>
        </div>
        <!--Data + Photo-->
        <div class="content">
            <object-detail
                v-if="data"
                class="data"
                :displayObject="data"
                extendedCellClass="printDataCell"
            />
            <div class="photo">
                <img
                    v-if="firstPhoto"
                    :src="firstPhoto"
                    alt="feedback image"
                    class="image"
                    @load="$_imgLoaded"
                />
                <div v-else>{{ $t('photoNotAvailable') }}</div>
            </div>
        </div>
        <!--Map-->
        <div>
            <mini-map
                :id="miniMapId"
                :ref="miniMapId"
                class="map-preview"
                :showControls="false"
            />
        </div>
    </div>
</template>

<script>
    import { mapState, mapGetters } from 'vuex';
    import {
        NEDGLOBE_VIEWER,
        DEFAULT_FEATURE_STYLE_SETTINGS,
        NG_LAYER_TYPES,
        MAP_MARGINS,
        FIELD_TYPE,
        DEFAULT_MIN_ZOOM_RESOLUTION
    } from '../../../constants';
    import { paperSizes, PAPER_FORMATS } from '../../../constants/print';
    import { prepareLayer } from '../../../services/map';
    import { getStyleSettings, getStyle } from '../../../services/map/layer/styles';
    import MiniMap from '../../geo/maps/Mini.vue';
    import ObjectDetail from '../../geo/information/feature/ObjectDetail';
    import { getImageUrlFromValue } from '../../../services/utilities';

    export default {
        components: {
            'object-detail': ObjectDetail,
            'mini-map': MiniMap
        },
        props: {
            panelTitle: { type: String, required: false },
            displayObject: { type: Object, required: false },
            isFeedBack: { type: Boolean, required: false }
        },
        created() {
            this.$_showFeatureOnMap();
        },
        data() {
            return {
                miniMapId: 'report-print-preview',
                layer: null,
                customLayerStyle: DEFAULT_FEATURE_STYLE_SETTINGS,
                previewStyle: NEDGLOBE_VIEWER.styleDisplayTypes.default
            };
        },
        computed: {
            ...mapState(['uiElements']),
            ...mapGetters(['getUserInfo']),
            menuComponents() {
                return this.uiElements.menuBar.components;
            },
            firstPhoto() {
                const photo = this.displayObject.printInfo?.filter(
                    (item) => item.type === FIELD_TYPE.FOTO
                );
                if (photo?.length > 0 && photo[0]?.value) {
                    return getImageUrlFromValue(photo[0].value);
                } else {
                    return null;
                }
            },
            data() {
                let info = [];
                if (this.isFeedBack) { // feedback
                    info = this.displayObject.printInfo?.filter(
                        (item) => item.type !== FIELD_TYPE.FOTO
                    );
                } else {              // layer
                    info = this.displayObject.info;
                }
                return {
                    ...this.displayObject,
                    info
                };
            },
            paperSizeStyle() {
                const foundSize = paperSizes.find(
                    (paperFormat) => paperFormat.name === PAPER_FORMATS.A4_PORTRAIT
                );

                const margin = 10;
                const height = foundSize.height - margin * 2;
                const width = foundSize.width - margin * 2;

                return {
                    top: `${margin}mm`,
                    left: `${margin}mm`,
                    width: `${width}mm`,
                    // -1 prevents extra page
                    height: `${height - 1}mm`,
                    overflow: 'hidden'
                };
            }
        },
        methods: {
            $_imgLoaded() {
                this.$emit('imageReady');
            },
            async $_setStyle() {
                const features = this.layer.getSource().getFeatures();
                const computedStyles = await getStyleSettings([
                    this.customLayerStyle
                ]);
                const styleFunction = (feature, resolution) => {
                    return getStyle(
                        computedStyles,
                        feature,
                        resolution,
                        this.previewStyle
                    );
                };
                features.forEach((feature) => {
                    feature.setStyle(styleFunction);
                });
            },
            async $_showFeatureOnMap() {
                if (this.data.feature) {
                    const data = {
                        data: [this.data.feature],
                        id: new Date().getTime().toString(),
                        name: 'feedbackPrint',
                        serviceType: 'feature'
                    };
                    const olLayer = await prepareLayer(this.$store, null, {
                        type: NG_LAYER_TYPES.CUSTOM,
                        data: data,
                        extended: data
                    });
                    this.layer = olLayer;
                    this.$nextTick(() => {
                        this.$refs[this.miniMapId].addCustomLayer(olLayer);
                        const margin = MAP_MARGINS.featuresZoom;

                        const mapConfig = this.$store.state.maps.find(
                            (_map) => _map.name === this.miniMapId
                        );
                        const view = mapConfig.map.getView();
                        const dataExtent = olLayer.getSource().getExtent();
                        const fitOptions = {
                            padding: [margin, margin, margin, margin],
                            minResolution: DEFAULT_MIN_ZOOM_RESOLUTION
                        };
                        view.fit(dataExtent, fitOptions);

                        mapConfig.map.once('rendercomplete', async () => {
                            this.$emit('mapReady');
                            if (!this.firstPhoto) {
                                this.$emit('imageReady');
                            }
                        });
                    });
                }
            }
        },
        watch: {
            layer: {
                handler: function (olLayer) {
                    if (olLayer) {
                        this.$_setStyle();
                    }
                },
                immediate: true
            }
        }
    };
</script>

<style lang="scss">
.printDataCell {
    width: auto !important;
    padding: 1px 10px 1px 5px !important;
    line-height: inherit !important;
    font-size: 85% !important;
}
</style>
<style scoped lang="scss">
.print-container {
    height: 100%;
    display: flex;
    flex-direction: column;

    .header {
        display: flex;
        justify-content: space-between;
        padding-bottom: 5px;

        .title {
            font-weight: bold;
            font-size: 22px;
        }

        .logo-container {
            align-items: center;
            display: flex;

            .image {
                background-size: contain;
                background-repeat: no-repeat;
                height: 45px;
            }
        }
    }

    .content {
        display: flex;
        flex: 1;
        overflow: hidden;
        height: 100%;
        margin-bottom: 1rem;

        .data {
            border: 1px solid $gray-04;
            flex: 1;
            margin-right: 1rem;
            display: inline;
            width: initial;
        }

        .photo {
            border: 1px solid $gray-04;
            flex: 1;
            display: flex;
            justify-content: center;
            align-items: center;
            width: 50%;

            .image {
                background-size: contain;
                background-repeat: no-repeat;
                max-width: 90%;
            }
        }
    }

    .map-preview {
        border: 1px solid $gray-04;
        width: 100%;
        height: 400px;
    }
}
</style>
